import React, { useState } from 'react';
import { MapPin, Phone, Mail, Calendar, ChevronDown, Star, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { therapies, contactInfo, testimonials } from '../mock';
import { useToast } from '../hooks/use-toast';

const Home = () => {
  const { toast } = useToast();
  const [bookingForm, setBookingForm] = useState({
    name: '',
    email: '',
    phone: '',
    therapy: '',
    message: ''
  });

  const [leadForm, setLeadForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [evaluationForm, setEvaluationForm] = useState({
    name: '',
    email: '',
    phone: '',
    painLevel: '',
    anxietyLevel: '',
    description: ''
  });

  const handleBookingSubmit = (e) => {
    e.preventDefault();
    
    // Create WhatsApp message with form data
    const message = `*Nova Marcação de Consulta*%0A%0A` +
      `*Nome:* ${bookingForm.name}%0A` +
      `*Email:* ${bookingForm.email}%0A` +
      `*Telefone:* ${bookingForm.phone}%0A` +
      `*Terapia:* ${bookingForm.therapy}%0A` +
      `*Mensagem:* ${bookingForm.message || 'Nenhuma mensagem'}`;
    
    // Open WhatsApp with pre-filled message
    window.open(`https://wa.me/351961509484?text=${message}`, '_blank');
    
    // Clear form
    setBookingForm({ name: '', email: '', phone: '', therapy: '', message: '' });
    
    toast({
      title: "Redirecionando para WhatsApp",
      description: "Complete o envio da marcação pelo WhatsApp.",
    });
  };

  const handleLeadSubmit = (e) => {
    e.preventDefault();
    
    // Create WhatsApp message with form data
    const message = `*Nova Subscrição Newsletter*%0A%0A` +
      `*Nome:* ${leadForm.name}%0A` +
      `*Email:* ${leadForm.email}%0A` +
      `*Telefone:* ${leadForm.phone || 'Não fornecido'}%0A` +
      `*Mensagem:* ${leadForm.message || 'Nenhuma mensagem'}`;
    
    // Open WhatsApp with pre-filled message
    window.open(`https://wa.me/351961509484?text=${message}`, '_blank');
    
    // Clear form
    setLeadForm({ name: '', email: '', phone: '', message: '' });
    
    toast({
      title: "Redirecionando para WhatsApp",
      description: "Complete a subscrição pelo WhatsApp.",
    });
  };

  const handleEvaluationSubmit = (e) => {
    e.preventDefault();
    
    // Create WhatsApp message with form data
    const message = `*Nova Avaliação de Paciente*%0A%0A` +
      `*Nome:* ${evaluationForm.name}%0A` +
      `*Email:* ${evaluationForm.email}%0A` +
      `*Telefone:* ${evaluationForm.phone}%0A` +
      `*Nível de Dor:* ${evaluationForm.painLevel}/10%0A` +
      `*Nível de Ansiedade:* ${evaluationForm.anxietyLevel}/10%0A` +
      `*Descrição:* ${evaluationForm.description}`;
    
    // Open WhatsApp with pre-filled message
    window.open(`https://wa.me/351961509484?text=${message}`, '_blank');
    
    // Clear form
    setEvaluationForm({ name: '', email: '', phone: '', painLevel: '', anxietyLevel: '', description: '' });
    
    toast({
      title: "Redirecionando para WhatsApp",
      description: "Complete o envio da avaliação pelo WhatsApp.",
    });
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-stone-200">
        <nav className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <img 
                src="/logo.png"
                alt="Samuel Magalhães - MTC"
                className="h-10 w-10 object-contain"
              />
              <div>
                <div className="font-semibold text-stone-900">Samuel Magalhães</div>
                <div className="text-xs text-stone-600">Medicina Tradicional Chinesa</div>
              </div>
            </div>
            <div className="hidden md:flex items-center space-x-6">
              <button onClick={() => scrollToSection('sobre')} className="text-stone-700 hover:text-sage-600 transition-colors">Sobre</button>
              <button onClick={() => scrollToSection('terapias')} className="text-stone-700 hover:text-sage-600 transition-colors">Terapias</button>
              <button onClick={() => scrollToSection('marcacoes')} className="text-stone-700 hover:text-sage-600 transition-colors">Marcações</button>
              <button onClick={() => scrollToSection('contactos')} className="text-stone-700 hover:text-sage-600 transition-colors">Contactos</button>
              <Button onClick={() => scrollToSection('marcacoes')} className="bg-sage-600 hover:bg-sage-700 text-white">Agendar Consulta</Button>
            </div>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-block">
                <span className="px-4 py-2 bg-sage-100 text-sage-800 rounded-full text-sm font-medium">Porto & Braga</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-stone-900 leading-tight">
                Terapeuta de Medicina Tradicional Chinesa no Porto e Braga
              </h1>
              <p className="text-lg text-stone-600 leading-relaxed">
                Procura um terapeuta experiente em Medicina Tradicional Chinesa? Especializado em acupuntura, shiatsu, auriculoterapia, craniopuntura, reflexologia, Tui-Na e massagem terapêutica. Através de terapias naturais, ajudo os meus pacientes a recuperar o equilíbrio físico e emocional.
              </p>
              
              {/* CTAs Maiores */}
              <div className="flex flex-col gap-4 pt-4">
                <a href="tel:961509484" className="w-full">
                  <Button size="lg" className="w-full bg-sage-600 hover:bg-sage-700 text-white text-lg py-6">
                    <Phone className="mr-3 h-6 w-6" />
                    Ligar Agora: 961 509 484
                  </Button>
                </a>
                <a href="https://wa.me/351961509484" target="_blank" rel="noopener noreferrer" className="w-full">
                  <Button size="lg" className="w-full bg-green-600 hover:bg-green-700 text-white text-lg py-6">
                    <MessageCircle className="mr-3 h-6 w-6" />
                    WhatsApp: 961 509 484
                  </Button>
                </a>
                <Button onClick={() => scrollToSection('marcacoes')} size="lg" variant="outline" className="border-sage-600 text-sage-700 hover:bg-sage-50 py-6">
                  <Calendar className="mr-2 h-5 w-5" />
                  Marcar Consulta Online
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_saude-tradicional/artifacts/zxkylia7_IMG-20251128-WA0009.jpg" 
                  alt="Medicina Tradicional Chinesa" 
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-900/20 to-transparent"></div>
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="text-3xl font-bold text-sage-600">4+</div>
                  <div className="text-sm text-stone-600">Anos de<br/>Experiência</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Apresentação do Terapeuta */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="relative order-2 md:order-1">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_saude-tradicional/artifacts/tqzuu95d_foto%20site%20samu.jpg" 
                    alt="Samuel Magalhães - Terapeuta de Medicina Tradicional Chinesa" 
                    className="w-full h-[600px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-stone-900/40 to-transparent"></div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="bg-white/95 backdrop-blur-sm p-6 rounded-xl">
                      <h3 className="text-2xl font-bold text-stone-900 mb-2">Samuel Magalhães</h3>
                      <p className="text-sage-600 font-semibold">Terapeuta de Medicina Tradicional Chinesa</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="space-y-6 order-1 md:order-2">
                <div className="inline-block">
                  <span className="px-4 py-2 bg-sage-100 text-sage-800 rounded-full text-sm font-semibold">Formação Profissional</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-stone-900">
                  Experiência e Formação Especializada
                </h2>
                <p className="text-lg text-stone-600 leading-relaxed">
                  Com uma sólida formação em terapias orientais e naturais, dedico-me a proporcionar tratamentos personalizados que promovem o bem-estar físico e emocional dos meus pacientes.
                </p>
                
                <div className="space-y-4">
                  <Card className="border-l-4 border-l-sage-600 shadow-sm">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-sage-100 flex items-center justify-center flex-shrink-0">
                          <div className="w-6 h-6 rounded-full bg-sage-600"></div>
                        </div>
                        <div>
                          <h3 className="font-bold text-stone-900 mb-1">Medicina Tradicional Chinesa</h3>
                          <p className="text-stone-600">Formado pelo Instituto Português de Naturologia de Braga</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-l-4 border-l-sage-600 shadow-sm">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-sage-100 flex items-center justify-center flex-shrink-0">
                          <div className="w-6 h-6 rounded-full bg-sage-600"></div>
                        </div>
                        <div>
                          <h3 className="font-bold text-stone-900 mb-1">YNSA - Craniopuntura de Yamamoto</h3>
                          <p className="text-stone-600">Certificado pelo Instituto Juan Hahn</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-l-4 border-l-sage-600 shadow-sm">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-sage-100 flex items-center justify-center flex-shrink-0">
                          <div className="w-6 h-6 rounded-full bg-sage-600"></div>
                        </div>
                        <div>
                          <h3 className="font-bold text-stone-900 mb-1">Kinesologia</h3>
                          <p className="text-stone-600">Especialização em técnicas de kinesologia aplicada</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-l-4 border-l-sage-600 shadow-sm">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-sage-100 flex items-center justify-center flex-shrink-0">
                          <div className="w-6 h-6 rounded-full bg-sage-600"></div>
                        </div>
                        <div>
                          <h3 className="font-bold text-stone-900 mb-1">Cédula Profissional ACSS</h3>
                          <p className="text-stone-600">Cédula Profissional de Medicina Tradicional Chinesa N.º C-0062232 (ACSS)</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div className="pt-4">
                  <p className="text-stone-700 leading-relaxed italic">
                    "A minha abordagem centra-se no paciente como um todo, combinando técnicas ancestrais da Medicina Tradicional Chinesa com métodos modernos para alcançar resultados eficazes e duradouros."
                  </p>
                  <p className="text-sage-700 font-semibold mt-2">— Samuel Magalhães</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sobre Section */}
      <section id="sobre" className="py-16 md:py-24 bg-sage-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900">Porquê Escolher Medicina Tradicional Chinesa?</h2>
            <p className="text-lg text-stone-600 leading-relaxed">
              Com mais de 4 anos de experiência em Medicina Tradicional Chinesa, trabalho com foco no reequilíbrio energético, prevenção de doenças e promoção do bem-estar global. Atendo pacientes das zonas do Porto, Braga e arredores, oferecendo um atendimento personalizado e holístico.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 pt-8">
              <div className="space-y-2">
                <div className="text-3xl font-bold text-sage-600">500+</div>
                <div className="text-sm text-stone-600">Pacientes Tratados</div>
              </div>
              <div className="space-y-2">
                <div className="text-3xl font-bold text-sage-600">6</div>
                <div className="text-sm text-stone-600">Terapias Disponíveis</div>
              </div>
              <div className="space-y-2">
                <div className="text-3xl font-bold text-sage-600">4+</div>
                <div className="text-sm text-stone-600">Anos Experiência</div>
              </div>
              <div className="space-y-2">
                <div className="text-3xl font-bold text-sage-600">98%</div>
                <div className="text-sm text-stone-600">Satisfação</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefícios Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4">Benefícios das Terapias</h2>
            <p className="text-lg text-stone-600">Resultados comprovados em diversas áreas da saúde</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {[
              { title: 'Alívio de Dores', desc: 'Lombares, cervicais e articulares' },
              { title: 'Melhoria do Sono', desc: 'E da digestão' },
              { title: 'Redução de Stress', desc: 'E ansiedade' },
              { title: 'Energia Vital', desc: 'Estimulação do Qi' }
            ].map((benefit, idx) => (
              <Card key={idx} className="border-none shadow-sm hover:shadow-md transition-shadow">
                <CardHeader>
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-sage-100 flex items-center justify-center mb-3 md:mb-4">
                    <div className="w-5 h-5 md:w-6 md:h-6 rounded-full bg-sage-600"></div>
                  </div>
                  <CardTitle className="text-lg md:text-xl">{benefit.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm md:text-base text-stone-600">{benefit.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Terapias Section */}
      <section id="terapias" className="py-16 md:py-24 bg-sage-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4">Terapias Disponíveis</h2>
            <p className="text-lg text-stone-600">Seis terapias especializadas para o seu bem-estar</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {therapies.map((therapy) => (
              <Card key={therapy.id} className="overflow-hidden border-none shadow-md hover:shadow-xl transition-all duration-300 group">
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={therapy.image} 
                    alt={therapy.name} 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-stone-900/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4">
                    <h3 className="text-2xl font-bold text-white">{therapy.name}</h3>
                  </div>
                </div>
                <CardContent className="p-6 space-y-4">
                  <p className="text-stone-600">{therapy.description}</p>
                  <div className="space-y-2">
                    <div className="font-semibold text-stone-900">Benefícios:</div>
                    <ul className="space-y-1">
                      {therapy.benefits.map((benefit, idx) => (
                        <li key={idx} className="text-sm text-stone-600 flex items-start">
                          <span className="mr-2 text-sage-600">•</span>
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4">Testemunhos</h2>
            <p className="text-lg text-stone-600">O que dizem os nossos pacientes</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-none shadow-sm">
                <CardContent className="p-6 space-y-4">
                  <div className="flex gap-1">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-stone-700 italic">"{testimonial.text}"</p>
                  <div>
                    <div className="font-semibold text-stone-900">{testimonial.name}</div>
                    <div className="text-sm text-stone-600">{testimonial.location}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Marcações Section */}
      <section id="marcacoes" className="py-16 md:py-24 bg-sage-50">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4">Marcar Consulta</h2>
              <p className="text-lg text-stone-600">Preencha o formulário e entraremos em contacto</p>
            </div>
            <Card className="shadow-lg border-none">
              <CardContent className="p-8">
                <form onSubmit={handleBookingSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="booking-name">Nome Completo *</Label>
                    <Input 
                      id="booking-name"
                      value={bookingForm.name}
                      onChange={(e) => setBookingForm({...bookingForm, name: e.target.value})}
                      required
                      className="border-stone-300 focus:border-sage-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="booking-email">E-mail *</Label>
                    <Input 
                      id="booking-email"
                      type="email"
                      value={bookingForm.email}
                      onChange={(e) => setBookingForm({...bookingForm, email: e.target.value})}
                      required
                      className="border-stone-300 focus:border-sage-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="booking-phone">Telefone *</Label>
                    <Input 
                      id="booking-phone"
                      type="tel"
                      value={bookingForm.phone}
                      onChange={(e) => setBookingForm({...bookingForm, phone: e.target.value})}
                      required
                      className="border-stone-300 focus:border-sage-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="booking-therapy">Tipo de Terapia *</Label>
                    <Select value={bookingForm.therapy} onValueChange={(value) => setBookingForm({...bookingForm, therapy: value})} required>
                      <SelectTrigger className="border-stone-300 focus:border-sage-500">
                        <SelectValue placeholder="Selecione uma terapia" />
                      </SelectTrigger>
                      <SelectContent>
                        {therapies.map((therapy) => (
                          <SelectItem key={therapy.id} value={therapy.id}>{therapy.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="booking-message">Mensagem / Observações</Label>
                    <Textarea 
                      id="booking-message"
                      value={bookingForm.message}
                      onChange={(e) => setBookingForm({...bookingForm, message: e.target.value})}
                      rows={4}
                      className="border-stone-300 focus:border-sage-500"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-sage-600 hover:bg-sage-700 text-white" size="lg">
                    <MessageCircle className="mr-2 h-5 w-5" />
                    Enviar via WhatsApp
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Lead Capture Section */}
      <section className="py-16 md:py-24 bg-sage-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Receba Dicas e Informações</h2>
            <p className="text-lg mb-8 text-sage-100">Subscreva para receber conteúdos sobre terapias naturais e bem-estar</p>
            <Card className="bg-white text-stone-900 border-none shadow-xl">
              <CardContent className="p-8">
                <form onSubmit={handleLeadSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="lead-name">Nome *</Label>
                    <Input 
                      id="lead-name"
                      value={leadForm.name}
                      onChange={(e) => setLeadForm({...leadForm, name: e.target.value})}
                      required
                      className="border-stone-300"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lead-email">E-mail *</Label>
                    <Input 
                      id="lead-email"
                      type="email"
                      value={leadForm.email}
                      onChange={(e) => setLeadForm({...leadForm, email: e.target.value})}
                      required
                      className="border-stone-300"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lead-phone">Contacto Telefónico</Label>
                    <Input 
                      id="lead-phone"
                      type="tel"
                      value={leadForm.phone}
                      onChange={(e) => setLeadForm({...leadForm, phone: e.target.value})}
                      className="border-stone-300"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lead-message">Mensagem (opcional)</Label>
                    <Textarea 
                      id="lead-message"
                      value={leadForm.message}
                      onChange={(e) => setLeadForm({...leadForm, message: e.target.value})}
                      rows={3}
                      className="border-stone-300"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-sage-600 hover:bg-sage-700 text-white" size="lg">
                    <MessageCircle className="mr-2 h-5 w-5" />
                    Enviar via WhatsApp
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Evaluation Form Section */}
      <section className="py-16 md:py-24 bg-stone-100">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4">Avaliação Gratuita</h2>
              <p className="text-lg text-stone-600">Preencha o questionário sobre dores e ansiedade</p>
            </div>
            <Card className="shadow-lg border-none">
              <CardContent className="p-8">
                <form onSubmit={handleEvaluationSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="eval-name">Nome Completo *</Label>
                    <Input 
                      id="eval-name"
                      value={evaluationForm.name}
                      onChange={(e) => setEvaluationForm({...evaluationForm, name: e.target.value})}
                      required
                      className="border-stone-300 focus:border-sage-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="eval-email">E-mail *</Label>
                    <Input 
                      id="eval-email"
                      type="email"
                      value={evaluationForm.email}
                      onChange={(e) => setEvaluationForm({...evaluationForm, email: e.target.value})}
                      required
                      className="border-stone-300 focus:border-sage-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="eval-phone">Telefone *</Label>
                    <Input 
                      id="eval-phone"
                      type="tel"
                      value={evaluationForm.phone}
                      onChange={(e) => setEvaluationForm({...evaluationForm, phone: e.target.value})}
                      required
                      className="border-stone-300 focus:border-sage-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="eval-pain">Nível de Dor (1-10) *</Label>
                    <Select value={evaluationForm.painLevel} onValueChange={(value) => setEvaluationForm({...evaluationForm, painLevel: value})} required>
                      <SelectTrigger className="border-stone-300 focus:border-sage-500">
                        <SelectValue placeholder="Selecione o nível" />
                      </SelectTrigger>
                      <SelectContent>
                        {[...Array(10)].map((_, i) => (
                          <SelectItem key={i+1} value={String(i+1)}>{i+1}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="eval-anxiety">Nível de Ansiedade (1-10) *</Label>
                    <Select value={evaluationForm.anxietyLevel} onValueChange={(value) => setEvaluationForm({...evaluationForm, anxietyLevel: value})} required>
                      <SelectTrigger className="border-stone-300 focus:border-sage-500">
                        <SelectValue placeholder="Selecione o nível" />
                      </SelectTrigger>
                      <SelectContent>
                        {[...Array(10)].map((_, i) => (
                          <SelectItem key={i+1} value={String(i+1)}>{i+1}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="eval-description">Descrição dos Sintomas *</Label>
                    <Textarea 
                      id="eval-description"
                      value={evaluationForm.description}
                      onChange={(e) => setEvaluationForm({...evaluationForm, description: e.target.value})}
                      rows={4}
                      required
                      className="border-stone-300 focus:border-sage-500"
                      placeholder="Descreva as suas dores, ansiedade ou outros sintomas..."
                    />
                  </div>
                  <Button type="submit" className="w-full bg-sage-600 hover:bg-sage-700 text-white" size="lg">
                    <MessageCircle className="mr-2 h-5 w-5" />
                    Enviar via WhatsApp
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4">Perguntas Frequentes</h2>
              <p className="text-lg text-stone-600">Respostas às questões mais comuns</p>
            </div>
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="item-1" className="border border-stone-200 rounded-lg px-6">
                <AccordionTrigger className="text-left font-semibold hover:text-sage-600">
                  A acupuntura dói?
                </AccordionTrigger>
                <AccordionContent className="text-stone-600">
                  A acupuntura utiliza agulhas muito finas e o desconforto é mínimo. A maioria dos pacientes sente apenas uma leve sensação de pressão. Muitos até relaxam durante a sessão.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2" className="border border-stone-200 rounded-lg px-6">
                <AccordionTrigger className="text-left font-semibold hover:text-sage-600">
                  Quantas sessões são necessárias?
                </AccordionTrigger>
                <AccordionContent className="text-stone-600">
                  Depende da condição a tratar. Problemas agudos podem melhorar em 3-5 sessões, enquanto condições crónicas podem requerer 8-12 sessões. Cada caso é avaliado individualmente.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3" className="border border-stone-200 rounded-lg px-6">
                <AccordionTrigger className="text-left font-semibold hover:text-sage-600">
                  A Medicina Tradicional Chinesa tem efeitos secundários?
                </AccordionTrigger>
                <AccordionContent className="text-stone-600">
                  As terapias de MTC são naturais e têm poucos ou nenhuns efeitos secundários quando realizadas por um terapeuta qualificado. Alguns pacientes podem sentir um leve cansaço após a primeira sessão.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-4" className="border border-stone-200 rounded-lg px-6">
                <AccordionTrigger className="text-left font-semibold hover:text-sage-600">
                  Posso combinar com tratamentos convencionais?
                </AccordionTrigger>
                <AccordionContent className="text-stone-600">
                  Sim, a Medicina Tradicional Chinesa é complementar e pode ser combinada com tratamentos médicos convencionais. Recomendo sempre informar o seu médico sobre todas as terapias que está a fazer.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* Contactos Section */}
      <section id="contactos" className="py-16 md:py-24 bg-sage-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4">Contactos</h2>
            <p className="text-lg text-stone-600">Entre em contacto para marcar a sua consulta</p>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="border-none shadow-md">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Phone className="h-5 w-5 text-sage-600" />
                    Telefone
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="text-lg font-semibold text-sage-600 hover:text-sage-700">
                    {contactInfo.phone}
                  </a>
                </CardContent>
              </Card>
              <Card className="border-none shadow-md">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-sage-600" />
                    E-mail
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <a href={`mailto:${contactInfo.email}`} className="text-lg font-semibold text-sage-600 hover:text-sage-700">
                    {contactInfo.email}
                  </a>
                </CardContent>
              </Card>
            </div>
            <Card className="mt-8 border-none shadow-md">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-sage-600" />
                  Locais de Atendimento
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-stone-600 mb-4">
                  Se procura acupuntura no Porto ou terapias naturais em Braga, atendo pacientes que sofrem de dores crónicas, tensões cervicais, ansiedade ou problemas articulares.
                </p>
                <div className="flex gap-4">
                  {contactInfo.locations.map((location, idx) => (
                    <div key={idx} className="px-4 py-2 bg-sage-100 text-sage-800 rounded-full font-medium">
                      {location}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-stone-900 text-stone-300 py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <img 
                  src="https://customer-assets.emergentagent.com/job_saude-tradicional/artifacts/kw6b12hg_cartao_samuel_magalhaes_frente_v2.png"
                  alt="Samuel Magalhães - MTC"
                  className="h-12 w-auto object-contain bg-white/10 p-1 rounded"
                />
                <div>
                  <div className="font-semibold text-white">Samuel Magalhães</div>
                  <div className="text-xs">Medicina Tradicional Chinesa</div>
                </div>
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-white mb-4">Navegação</h3>
              <ul className="space-y-2">
                <li><button onClick={() => scrollToSection('sobre')} className="hover:text-sage-400 transition-colors">Sobre</button></li>
                <li><button onClick={() => scrollToSection('terapias')} className="hover:text-sage-400 transition-colors">Terapias</button></li>
                <li><button onClick={() => scrollToSection('marcacoes')} className="hover:text-sage-400 transition-colors">Marcações</button></li>
                <li><button onClick={() => scrollToSection('contactos')} className="hover:text-sage-400 transition-colors">Contactos</button></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-white mb-4">Terapias</h3>
              <ul className="space-y-2">
                <li className="hover:text-sage-400 transition-colors">Acupuntura</li>
                <li className="hover:text-sage-400 transition-colors">Shiatsu</li>
                <li className="hover:text-sage-400 transition-colors">Reflexologia</li>
                <li className="hover:text-sage-400 transition-colors">Tui-Na</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-white mb-4">Contacto</h3>
              <ul className="space-y-2">
                <li className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="hover:text-sage-400 transition-colors">{contactInfo.phone}</a>
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  <a href={`mailto:${contactInfo.email}`} className="hover:text-sage-400 transition-colors">{contactInfo.email}</a>
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  <span>Porto & Braga</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-stone-800 pt-8 space-y-4">
            <div className="flex flex-wrap gap-4 justify-center text-sm">
              <Link to="/privacidade" className="hover:text-sage-400 transition-colors">Política de Privacidade</Link>
              <span>|</span>
              <Link to="/termos" className="hover:text-sage-400 transition-colors">Termos de Utilização</Link>
              <span>|</span>
              <a href="https://www.livroreclamacoes.pt" target="_blank" rel="noopener noreferrer" className="hover:text-sage-400 transition-colors">Livro de Reclamações</a>
            </div>
            <div className="text-center text-sm">
              <p>© 2025 Samuel Magalhães - Terapeuta de Medicina Tradicional Chinesa</p>
              <p className="text-stone-500 mt-2">Porto | Braga | Portugal</p>
            </div>
            <div className="text-center text-xs text-stone-500 mt-4">
              <p>O website cumpre o Regulamento Geral sobre a Proteção de Dados (RGPD) e a Lei n.º 58/2019.</p>
              <p>Os dados recolhidos destinam-se apenas à comunicação com o cliente e marcação de consultas.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;